import type { FC } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

export const HomeHero: FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Animation variants
  const leftVariants = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };
  const rightVariants = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut', delay: 0.2 } }
  };
  const statVariants = {
    initial: { y: 0 },
    animate: {
      y: [0, -8, 0],
      transition: { duration: 2, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut' }
    }
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        pt: { xs: 12, md: 0 },
        mb: "50px",
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0,0,0,0.6)), url("https://as2.ftcdn.net/jpg/09/23/24/79/1000_F_923247948_g11xdyjtnZAu6IoiW79WMfpKqNEkdlH6.jpg")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: '#fff'
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '100%',
          position: 'relative',
          zIndex: 1,
          gap: { xs: 8, md: 10 }
        }}
      >
        {/* Left Column - Text Content */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
          variants={leftVariants}
          style={{ flex: '1 1 55%', textAlign: 'inherit', maxWidth: '55%' }}
        >
          <Typography
            variant="overline"
            color="#FF6B00"
            sx={{
              mb: 2,
              display: 'block',
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: { xs: '0.875rem', md: '1rem' }
            }}
          >
            INSTITUTIONAL-GRADE PAYMENT INFRASTRUCTURE
          </Typography>

          <Typography
            variant="h1"
            sx={{
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.75rem' },
              lineHeight: 1.2,
              fontWeight: 700
            }}
          >
            Scale globally with{' '}
            <Typography
              component="span"
              color="#FF6B00"
              variant="inherit"
              sx={{ fontWeight: 700 }}
            >
              enterprise
            </Typography>{' '}
            confidence
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '1.125rem', md: '1.375rem' },
              fontWeight: 500,
              mb: 5,
              lineHeight: 1.6
            }}
          >
            Experience institutional-grade payment infrastructure that processes billions securely. 
            Built for enterprises, trusted by leaders, and engineered for seamless global operations.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 4,
              mb: 6,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', md: 'flex-start' }
            }}
          >
            <motion.div variants={statVariants} initial="initial" animate="animate">
              <Box>
                <Typography variant="h3" color="#FF6B00" sx={{ fontWeight: 700, mb: 1 }}>
                  $2B+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Transaction Volume
                </Typography>
              </Box>
            </motion.div>
            <motion.div variants={statVariants} initial="initial" animate="animate" transition={{ delay: 0.3 }}>
              <Box>
                <Typography variant="h3" color="#FF6B00" sx={{ fontWeight: 700, mb: 1 }}>
                  99.99%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Uptime SLA
                </Typography>
              </Box>
            </motion.div>
            <motion.div variants={statVariants} initial="initial" animate="animate" transition={{ delay: 0.6 }}>
              <Box>
                <Typography variant="h3" color="#FF6B00" sx={{ fontWeight: 700, mb: 1 }}>
                  30+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Global Markets
                </Typography>
              </Box>
            </motion.div>
          </Box>
        </motion.div>

        {/* Right Column - Engaging Content & CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
          variants={rightVariants}
          style={{ flex: '1 1 40%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 16, minWidth: '340px', maxWidth: '400px', margin: '0 auto' }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "white",
              fontWeight: 700,
              mb: 2,
              textAlign: 'center'
            }}
          >
            Ready to scale your business?
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1rem', md: '1rem' },
              fontWeight: 500,
              mb: 5,
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            Join leading enterprises leveraging our secure, global payment infrastructure. Start your journey with us today and unlock new opportunities.
          </Typography>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Button
              component="a"
              href="https://cal.com/support-team-qsszsx"
              target="_blank"
              variant="outlined"
              sx={{
                backgroundColor: 'transparent',
                color: '#FF6B00',
                borderColor: 'white',
                borderWidth: 1,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#FF6B00'
                }
              }}
            >
              Get Started
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};
